// UpgradePanel.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Subscription upgrade UI. Shows the plans a signed-in user can buy, calls the
// createCheckoutSession callable, and redirects to Stripe's hosted checkout.
// Paid users get a "Manage subscription" button into the Stripe billing portal.
//
// Tier flips AFTER payment via the stripeWebhook (sets the custom claim) and the
// client token refresh that userTier.js drives off tierUpdatedAt — so on return
// from a successful checkout we show a short "updating…" notice rather than
// expecting the tier to change the instant the page reloads.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useUserTier, useCurrentUser, TIERS } from "./userTier";
import "./UpgradePanel.css";

const createCheckoutSession = httpsCallable(getFunctions(), "createCheckoutSession");
const createPortalSession = httpsCallable(getFunctions(), "createPortalSession");

// Plans grouped by tier. priceId values are the live Stripe (test-mode) prices.
const TIER_CARDS = [
  {
    tier: TIERS.ARTIST,
    name: "Artist",
    mod: "up-card--artist",
    plans: [
      { interval: "Monthly", price: "$8/mo",  priceId: "price_1U3JZ7BUvkSfMt8vFlqQGS9K" },
      { interval: "Annual",  price: "$72/yr", priceId: "price_1U3JZ7BUvkSfMt8vnT7LSAxJ", note: "Save 25%" },
      { interval: "Lifetime", price: "$49 once", priceId: "price_1U3JZ7BUvkSfMt8vFlqQGS9K", note: "One-time" },
    ],
  },
  {
    tier: TIERS.STUDIO,
    name: "Studio",
    mod: "up-card--studio",
    plans: [
      { interval: "Monthly", price: "$19/mo",  priceId: "price_1U3JZ0BUvkSfMt8vHODfYTLx" },
      { interval: "Annual",  price: "$180/yr", priceId: "price_1U3JZ0BUvkSfMt8vYWgkAc2P", note: "Save ~20%" },
      { interval: "Lifetime", price: "$299 once", priceId: "price_1U3JZ0BUvkSfMt8vxuYS3fUb", note: "One-time" },
    ],
  },
];

// Which tiers are an upgrade from the current one (don't sell sideways or down).
const UPGRADE_TARGETS = {
  [TIERS.FREE]:   [TIERS.ARTIST, TIERS.STUDIO],
  [TIERS.ARTIST]: [TIERS.STUDIO],
  [TIERS.STUDIO]: [],
};

export default function UpgradePanel() {
  const tier = useUserTier();
  const user = useCurrentUser();
  const [busyPriceId, setBusyPriceId] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [error, setError] = useState("");
  const [returnState, setReturnState] = useState(null); // 'success' | 'cancel' | null

  // Detect return from Stripe checkout (?checkout=success|cancel) and clean URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get("checkout");
    if (c === "success" || c === "cancel") {
      setReturnState(c);
      params.delete("checkout");
      const qs = params.toString();
      window.history.replaceState(
        {}, "",
        window.location.pathname + (qs ? `?${qs}` : "")
      );
    }
  }, []);

  async function startCheckout(priceId) {
    setError("");
    setBusyPriceId(priceId);
    try {
      const { data } = await createCheckoutSession({
        priceId,
        origin: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else throw new Error("No checkout URL returned.");
    } catch (err) {
      console.error("[checkout] failed:", err);
      setError(err?.message || "Could not start checkout. Please try again.");
      setBusyPriceId(null);
    }
  }

  async function openPortal() {
    setError("");
    setPortalBusy(true);
    try {
      const { data } = await createPortalSession({ origin: window.location.origin });
      if (data?.url) window.location.href = data.url;
      else throw new Error("No portal URL returned.");
    } catch (err) {
      console.error("[portal] failed:", err);
      setError(err?.message || "Could not open billing portal. Please try again.");
      setPortalBusy(false);
    }
  }

  if (!user) return null;

  const buyable = UPGRADE_TARGETS[tier] || [];
  const visibleCards = TIER_CARDS.filter((c) => buyable.includes(c.tier));
  const isPaid = tier === TIERS.ARTIST || tier === TIERS.STUDIO;

  return (
    <div className="up-root">
      {returnState === "success" && (
        <div className="up-note up-note--ok">
          Payment received — updating your account. Your new tier appears in a few
          seconds.
        </div>
      )}
      {returnState === "cancel" && (
        <div className="up-note">Checkout canceled. No charge was made.</div>
      )}

      {/* Current-plan line: paid users always see where they stand. */}
      {isPaid && (
        <p className="up-current">
          You're on the <strong>{tier}</strong> plan — full access.
        </p>
      )}

      {/* Upgrade options, when there's a higher tier to move to. */}
      {visibleCards.length > 0 && (
        <>
          <p className="up-title">{isPaid ? "Move up a tier" : "Choose a plan"}</p>
          <div className="up-grid">
            {visibleCards.map((card) => (
              <div key={card.tier} className={`up-card ${card.mod}`}>
                <div className="up-tier-name">{card.name}</div>
                <div className="up-options">
                  {card.plans.map((p) => (
                    <button
                      key={p.priceId}
                      className="up-plan-btn"
                      disabled={busyPriceId !== null}
                      onClick={() => startCheckout(p.priceId)}
                    >
                      <span className="up-plan-interval">
                        {p.interval}
                        {p.note && <span className="up-plan-note">{p.note}</span>}
                      </span>
                      <span className="up-plan-price">
                        {busyPriceId === p.priceId ? (
                          <span className="up-plan-busy">redirecting…</span>
                        ) : (
                          p.price
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Manage subscription: any paid user can reach the billing portal to
          switch, downgrade, update payment, or cancel. */}
      {isPaid && (
        <button
          className="up-manage-btn"
          disabled={portalBusy}
          onClick={openPortal}
        >
          {portalBusy ? "Opening…" : "Manage subscription"}
        </button>
      )}

      {/* Free users with no cards shown (shouldn't happen, but safe). */}
      {!isPaid && visibleCards.length === 0 && (
        <p className="up-current">You're on the <strong>free</strong> plan.</p>
      )}

      {error && <div className="up-note up-note--err" style={{ marginTop: 12 }}>{error}</div>}
    </div>
  );
}
